import { ChakraProvider, Flex, extendTheme, useToast } from "@chakra-ui/react";
import Cookies from "js-cookie";
import momentTimeZone from "moment-timezone";
import type { NextComponentType } from "next";
import { SessionProvider } from "next-auth/react";
import { appWithTranslation, useTranslation } from "next-i18next";
import { AppProps } from "next/app";
import dynamic from "next/dynamic";
import Router, { useRouter } from "next/router";
import NProgress from "nprogress";
import React, { useEffect, useMemo } from "react";
import { MutationCache, QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { RecoilRoot } from "recoil";

import { ToastContent } from "design_system/components/Toast";
import graphQLClient from "graphql/react-query";
import { config, shouldHiddenIntercom } from "src/config";
import { DEFAULT_PHONE } from "src/constants/Common";
import { CURRENT_COUNTRY } from "src/constants/Countries";
import { AppTheme, baseTheme } from "src/themes";
import { createGlobalMutationErrorHandler } from "src/utils/globalMutationErrorHandler";
import { loadMomentLocale } from "src/utils/loadMomentLocale";

import "../styles/globals.css";
import "../styles/plans.css";

const MaintenancePage = dynamic(() => import("./MaintenancePage"));
const WholePageLoading = dynamic(() => import("components/Loading/WholePageLoading"));
const AuthWrapper = dynamic(() => import("src/wrappers/AuthWrapper"), {
  ssr: false
});
Router.events.on("routeChangeStart", () => {
  NProgress.start();
});
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

NProgress.configure({ showSpinner: false });

// set default timezone
momentTimeZone.tz.setDefault(process.env.NEXT_PUBLIC_TIMEZONE || "Asia/Singapore");

type CustomAppProps = AppProps & {
  Component: NextComponentType & { notAuth?: boolean };
};

const currentTheme = extendTheme({ ...AppTheme, ...baseTheme });

const MyApp = ({ Component, pageProps }: CustomAppProps) => {
  const { t } = useTranslation(["errors_messages"]);
  const toast = useToast();
  const router = useRouter();
  const { locale } = router;

  useEffect(() => {
    loadMomentLocale(locale);
  }, [locale]);

  useEffect(() => {
    // init accept-language header
    const currentLocale = Cookies.get(config.COOKIES.LOCALE);
    if (currentLocale !== locale) {
      // first load page with the stored locale
      if (locale) {
        Cookies.set(config.COOKIES.LOCALE, locale);
        graphQLClient.setHeader("accept-language", locale);
      } else if (currentLocale) {
        Cookies.set(config.COOKIES.LOCALE, currentLocale);
        graphQLClient.setHeader("accept-language", currentLocale);
      }
    }
  }, [locale]);

  useEffect(() => {
    if ((window as any).Intercom) {
      (window as any).Intercom("update", {
        hide_default_launcher: shouldHiddenIntercom(router.pathname)
      });
    }
  }, [router.pathname]);

  const reactQueryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false
          }
        },
        mutationCache: new MutationCache({
          onError: createGlobalMutationErrorHandler((errorCode) => {
            toast({
              render: ({ onClose }) => (
                <ToastContent
                  onClose={onClose}
                  message={t(errorCode, { phoneNumber: DEFAULT_PHONE[CURRENT_COUNTRY] })}
                />
              ),
              duration: 5000
            });
          })
        })
      }),
    [t, toast]
  );

  if (process.env.NEXT_PUBLIC_MAINTENANCE === "true") {
    return <MaintenancePage />;
  }

  return (
    <>
      <QueryClientProvider client={reactQueryClient}>
        <ChakraProvider theme={currentTheme}>
          <RecoilRoot>
            <React.Suspense fallback={<WholePageLoading />}>
              <SessionProvider session={pageProps.session}>
                {Component.notAuth ? (
                  <Component {...pageProps} />
                ) : (
                  <AuthWrapper>
                    <Flex className="AppTheme" flexDirection="column" minH="100vh">
                      <Component {...pageProps} />
                    </Flex>
                  </AuthWrapper>
                )}
              </SessionProvider>
            </React.Suspense>
          </RecoilRoot>
        </ChakraProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
};

export default appWithTranslation(MyApp);
